import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Card,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TablePagination,
  CircularProgress,
  TableContainer,
  Button,
  TableCell,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  Alert,
  Snackbar,
  Tooltip,
  Chip,
  Grid,
  Divider,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { green } from "@mui/material/colors";
import axios from "axios";
import { API_URL } from "../../../Config/api";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PendingActionsIcon from "@mui/icons-material/PendingActions";

/* ===================== STYLES ===================== */
const TABLE_HEADERS = [
  "Sr.No",
  "Event Code",
  "Start Date",
  "End Date",
  "Session",
  "Revalidation",
  "Prof Cost",
  "Total Cost",
  "Paid Sessions",
  "Feedback",
  "Payment Taken",
  "Action",
];

/* ===================== COMPONENT ===================== */

const ViewPayoutDetailsmodal = ({ row }) => {
  const accessToken = localStorage.getItem("token");
  const [tableData, setTableData] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [actionLoading, setActionLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");

  // Dialog states
  const [openApproveDialog, setOpenApproveDialog] = useState(false);

  // --- ALL DEDUCTION & REFUND STATES ---
  const [apronCharge, setApronCharge] = useState("");
  const [policeVerificationCharge, setPoliceVerificationCharge] = useState("");
  const [kitType, setKitType] = useState("");
  const [kitCharge, setKitCharge] = useState("");

  // NEW STATE FOR KIT CHARGE REFUND
  const [kitChargeRefund, setKitChargeRefund] = useState("");

  // NEW STATE FOR REMARK
  const [remark, setRemark] = useState("");

  // Helper function to allow only numbers in text fields
  const handleNumberInput = (setter) => (e) => {
    const value = e.target.value;
    // Allow empty string or numbers only
    if (value === "" || /^\d+$/.test(value)) {
      setter(value);
    }
  };

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const summary = useMemo(() => {
    const selectedItems = selectedRows.map((i) => tableData[i]).filter(Boolean);

    const totalProfessionalCost = selectedItems.reduce(
      (sum, item) => sum + Number(item.total_prof_cost || 0),
      0,
    );

    const conveyance = selectedItems.reduce(
      (sum, item) => sum + Number(item.convenience || 0),
      0,
    );

    const tds = totalProfessionalCost * 0.1;

    // Calculate specific deductions and refunds from text fields
    const apronDeduction = Number(apronCharge || 0);
    const policeDeduction = Number(policeVerificationCharge || 0);
    const kitDeduction = Number(kitCharge || 0);

    // NEW: Get Refund Amount
    const kitRefundAmount = Number(kitChargeRefund || 0);

    // NEW: Refund amount is ADDED (+) to the net amount
    const netAmount =
      totalProfessionalCost -
      tds -
      apronDeduction -
      policeDeduction -
      kitDeduction +
      kitRefundAmount + // Addition logic here
      conveyance;

    return {
      totalProfessionalCost,
      tds,
      conveyance,
      apronDeduction,
      policeDeduction,
      kitDeduction,
      kitRefundAmount, // Exported to summary
      netAmount,
    };
  }, [
    selectedRows,
    tableData,
    apronCharge,
    policeVerificationCharge,
    kitCharge,
    kitChargeRefund, // Added as dependency
  ]);

  const handleCheckboxToggle = (rowIndex) => {
    setSelectedRows((prev) =>
      prev.includes(rowIndex)
        ? prev.filter((i) => i !== rowIndex)
        : [...prev, rowIndex],
    );
  };

  const paginatedData = useMemo(() => {
    if (!Array.isArray(tableData)) return [];
    return tableData.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage,
    );
  }, [tableData, page, rowsPerPage]);

  const fetchDetails = async () => {
    try {
      setLoading(true);
      setStatusMessage("");
      const response = await axios.get(`${API_URL}/pay_out/prof_dtl_unit/`, {
        params: {
          start_date: row.start_date,
          end_date: row.end_date,
          srv_prof_ids: row.srv_prof_id,
        },
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const apiData = response.data;
      if (Array.isArray(apiData) && apiData.length > 0) {
        setTableData(apiData);
      } else if (
        apiData.data &&
        Array.isArray(apiData.data) &&
        apiData.data.length > 0
      ) {
        setTableData(apiData.data);
      } else {
        setTableData([]);
        if (apiData.status) setStatusMessage(apiData.status);
      }
    } catch (error) {
      console.error("View API error", error);
      setTableData([]);
      if (error.response?.data?.status)
        setStatusMessage(error.response.data.status);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!row) return;
    fetchDetails();
  }, [row]);

  const handleApprove = async () => {
    if (selectedRows.length === 0) {
      setSnackbar({
        open: true,
        message: "Please select at least one event",
        severity: "warning",
      });
      return;
    }
    setActionLoading(true);

    try {
      const selectedItems = selectedRows
        .map((i) => tableData[i])
        .filter(Boolean);

      const events = selectedItems.map((item) => ({
        eve_id: item.eve_id,
        service_start_date: item.service_start_date,
        service_end_date: item.service_end_date,
        session_count: item.total_sessions,
        revalidation_session: item.revalidated_session,
        session_paid: item.session_paid,
        prof_cost: item.prof_cost,
        total_prof_cost: item.total_prof_cost,
        feed_back_status: item.feedback || false,
        payment_with_status: item.payment_status ?? 1,
        revalidated_session_ids: item.revalidated_session_ids || [],
      }));

      const totalRequestedAmount = summary.totalProfessionalCost;
      const TDS_deducted_amount = summary.tds;
      const convenience = summary.conveyance;

      const payload = {
        srv_prof_id: row.srv_prof_id,
        total_requeststed_amount: totalRequestedAmount,
        TDS_deducted_amount,
        convenience,
        approne_charges: summary.apronDeduction,
        approne_charges_status: summary.apronDeduction > 0 ? 2 : 1,
        police_verification_charges: summary.policeDeduction,
        police_verification_charges_status: summary.policeDeduction > 0 ? 2 : 1,
        kit_type: kitType,
        kit_charges: summary.kitDeduction,
        
        kit_charge_refund: summary.kitRefundAmount, // Added Refund field to payload
        remark: remark,
        net_amount: summary.netAmount,
        events,
      };

      await axios.post(`${API_URL}/pay_out/prof_dtl_unit_request/`, payload, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      setSnackbar({
        open: true,
        message: "Payout approved successfully",
        severity: "success",
      });

      // Reset selection and deductions
      setSelectedRows([]);
      setApronCharge("");
      setPoliceVerificationCharge("");
      setKitType("");
      setKitCharge("");
      setKitChargeRefund(""); // Reset Refund state
      setRemark("");

      fetchDetails();
      setOpenApproveDialog(false);
    } catch (error) {
      console.error("Approve API error", error);
      setSnackbar({
        open: true,
        message: "Something went wrong while approving payout",
        severity: "error",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const isBankVerified =
    row?.is_bank_verified === true ||
    row?.is_bank_verified === "true" ||
    row?.prof_bank_verification === "true" ||
    row?.prof_bank_verification === true;

  if (!row) return null;

  if (loading) {
    return (
      <Box sx={{ height: "60vh", display: "grid", placeItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {isBankVerified ? (
        <Alert
          severity="success"
          variant="outlined"
          sx={{
            mb: 2,
            borderRadius: 2,
            fontWeight: 600,
            backgroundColor: "#f0fdf4",
            borderColor: green[200],
          }}
        >
          Verification Success: Professional's bank account is verified.
        </Alert>
      ) : (
        <Alert
          severity="warning"
          variant="filled"
          icon={<WarningAmberIcon fontSize="inherit" />}
          sx={{ mb: 2, borderRadius: 2, fontWeight: 600 }}
        >
          Attention: Bank details are NOT verified. Payout approval is blocked.
        </Alert>
      )}

      {/* TABLE CARD */}
      <Card sx={{ p: 2, borderRadius: 2 }}>
        <TableContainer
          sx={{ width: "100%", overflowX: "auto", maxHeight: "50vh" }}
        >
          <Table stickyHeader sx={{ minWidth: 1200 }}>
            <TableHead>
              <TableRow>
                {TABLE_HEADERS?.map((h, i) => (
                  <TableCell
                    key={h}
                    align="center"
                    sx={{
                      backgroundColor: "#a6b4be",
                      fontWeight: 700,
                      color: "#000",
                      whiteSpace: "nowrap",
                      borderRight:
                        i === TABLE_HEADERS.length - 1
                          ? "none"
                          : "1px solid #fff",
                      borderTopLeftRadius: i === 0 ? 12 : 0,
                      borderTopRightRadius:
                        i === TABLE_HEADERS.length - 1 ? 12 : 0,
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.length > 0 ? (
                paginatedData.map((item, index) => {
                  const rowIndex = page * rowsPerPage + index;
                  const isChecked = selectedRows.includes(rowIndex);
                  return (
                    <TableRow
                      key={rowIndex}
                      hover
                      sx={{ backgroundColor: "#fff" }}
                    >
                      <TableCell align="center">{rowIndex + 1}</TableCell>
                      <TableCell align="center">{item.event_code}</TableCell>
                      <TableCell align="center">
                        {item.service_start_date}
                      </TableCell>
                      <TableCell align="center">
                        {item.service_end_date}
                      </TableCell>
                      <TableCell align="center">
                        {item.total_sessions}
                      </TableCell>
                      <TableCell align="center">
                        {item.revalidated_session}
                      </TableCell>
                      <TableCell align="center">₹{item.prof_cost}</TableCell>
                      <TableCell align="center">
                        ₹{item.total_prof_cost}
                      </TableCell>
                      <TableCell align="center">{item.session_paid}</TableCell>
                      <TableCell align="center">
                        <Tooltip
                          title={
                            item.feed_back_status
                              ? "Feedback Received"
                              : "Pending Feedback"
                          }
                        >
                          <span>
                            {item.feed_back_status ? (
                              <CheckCircleIcon color="success" />
                            ) : (
                              <PendingActionsIcon color="action" />
                            )}
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={
                            item.payment_status === 1
                              ? "Unpaid"
                              : item.payment_status === 2
                                ? "By professional"
                                : item.payment_status === 3
                                  ? "By Desk"
                                  : item.payment_status === 4
                                    ? "Complimentary"
                                    : "Unknown Status"
                          }
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: "11px",
                            px: 1,
                            backgroundColor:
                              item.payment_status === 1
                                ? "#ffebee"
                                : item.payment_status === 2
                                  ? "#e3f2fd"
                                  : item.payment_status === 3
                                    ? "#e8f5e9"
                                    : item.payment_status === 4
                                      ? "#bbdefb"
                                      : "#f5f5f5",
                            color:
                              item.payment_status === 1
                                ? "#c62828"
                                : item.payment_status === 2
                                  ? "#1565c0"
                                  : item.payment_status === 3
                                    ? "#2e7d32"
                                    : item.payment_status === 4
                                      ? "#0d47a1"
                                      : "#616161",
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          size="small"
                          checked={isChecked}
                          onChange={() => handleCheckboxToggle(rowIndex)}
                          color="success"
                        />
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={TABLE_HEADERS.length}
                    align="center"
                    sx={{ py: 5 }}
                  >
                    <Typography
                      variant="body1"
                      sx={{ color: "#666", fontWeight: 500 }}
                    >
                      {statusMessage ||
                        "No records found for the selected criteria."}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          sx={{
            overflow: "hidden",
            "& .MuiTablePagination-toolbar": { paddingRight: 1 },
          }}
          component="div"
          count={tableData.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(+e.target.value);
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </Card>

      {/* ===================== SUMMARY & DEDUCTIONS ===================== */}
      <AnimatePresence>
        {selectedRows.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            <Card sx={{ mt: 3, borderRadius: 2, background: "#FAFAFA", p: 2 }}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, color: "#424242", mb: 2 }}
              >
                Professional Deductions & Adjustments
              </Typography>

              {/* GRID UPDATED WITH REFUND FIELD */}
              <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4} md={2}>
                  <TextField
                    label="Apron Charge (₹)"
                    type="text"
                    size="small"
                    fullWidth
                    value={apronCharge}
                    onChange={handleNumberInput(setApronCharge)}
                  />
                </Grid>

                <Grid item xs={12} sm={4} md={2}>
                  <TextField
                    label="Police Verif. (₹)"
                    type="text"
                    size="small"
                    fullWidth
                    value={policeVerificationCharge}
                    onChange={handleNumberInput(setPoliceVerificationCharge)}
                  />
                </Grid>

                <Grid item xs={12} sm={4} md={2}>
                  <TextField
                    label="Kit Name/Type"
                    type="text"
                    size="small"
                    fullWidth
                    value={kitType}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^a-zA-Z\s]/g, "");
                      setKitType(value);
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={4} md={2}>
                  <TextField
                    label="Kit Charge Deduction (₹)"
                    type="text"
                    size="small"
                    fullWidth
                    value={kitCharge}
                    onChange={handleNumberInput(setKitCharge)}
                  />
                </Grid>

                {/* --- NEW FIELD: KIT CHARGE REFUND --- */}
                <Grid item xs={12} sm={4} md={2}>
                  <TextField
                    label="Kit Charges Refund (₹)"
                    type="text"
                    size="small"
                    fullWidth
                    value={kitChargeRefund}
                    onChange={handleNumberInput(setKitChargeRefund)}
                  />
                </Grid>

                <Grid item xs={12} sm={4} md={2}>
                  <TextField
                    label="Remark"
                    type="text"
                    size="small"
                    fullWidth
                    value={remark}
                    onChange={(e) => setRemark(e.target.value)}
                    placeholder="Enter remarks..."
                  />
                </Grid>
              </Grid>

              <Divider sx={{ mb: 2 }} />

              {/* Summary Table */}
              <Table
                size="small"
                sx={{
                  "& th, & td": { textAlign: "center", fontSize: 13 },
                  "& th": { fontWeight: 700, backgroundColor: "#F1F8E9" },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>Total Prof Cost</TableCell>
                    <TableCell>Conveyance</TableCell>
                    <TableCell sx={{ color: "error.main" }}>
                      TDS (10%)
                    </TableCell>
                    <TableCell sx={{ color: "error.main" }}>
                      Total Deductions
                    </TableCell>
                    {/* ADDED COLUMN FOR REFUND */}
                    <TableCell sx={{ color: "success.main" }}>
                     Kit Charges Refund (+)
                    </TableCell>
                    <TableCell sx={{ fontSize: 14 }}>Net Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      ₹{summary.totalProfessionalCost.toFixed(2)}
                    </TableCell>
                    <TableCell>₹{summary.conveyance.toFixed(2)}</TableCell>
                    <TableCell sx={{ color: "error.main" }}>
                      -₹{summary.tds.toFixed(2)}
                    </TableCell>
                    <TableCell sx={{ color: "error.main" }}>
                      -₹
                      {(
                        summary.apronDeduction +
                        summary.policeDeduction +
                        summary.kitDeduction
                      ).toFixed(2)}
                    </TableCell>
                    {/* ADDED DATA FOR REFUND */}
                    <TableCell
                      sx={{ color: "success.main", fontWeight: "bold" }}
                    >
                      +₹{summary.kitRefundAmount.toFixed(2)}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 800,
                        fontSize: 16,
                        color: summary.netAmount <= 0 ? "#d32f2f" : "#2E7D32",
                      }}
                    >
                      ₹{summary.netAmount.toFixed(2)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>

              {/* Deduction Breakdown Caption */}
              <Box sx={{ mt: 1, textAlign: "center" }}>
                <Typography variant="caption" color="textSecondary">
                  (Deductions: Apron -₹{summary.apronDeduction || 0} | Police -₹
                  {summary.policeDeduction || 0} | Kit -₹
                  {summary.kitDeduction || 0}){" "}
                  <strong>
                    {" "}
                    | Kit Refund: +₹{summary.kitRefundAmount || 0}
                  </strong>
                </Typography>
              </Box>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ===================== ACTION BAR ===================== */}
      <Box
        sx={{
          mt: 3,
          p: 2,
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          borderTop: "1px dashed #CFCFCF",
        }}
      >
        <Tooltip
          title={
            !isBankVerified
              ? "Bank details not verified. Payout blocked."
              : selectedRows.length === 0
                ? "Please select at least one event to approve."
                : summary.netAmount <= 0
                  ? `Net Amount (₹${summary.netAmount.toFixed(
                      2,
                    )}) must be greater than zero.`
                  : ""
          }
          arrow
          placement="top"
        >
          <span>
            <Button
              variant="contained"
              color="success"
              disabled={
                actionLoading ||
                selectedRows.length === 0 ||
                !isBankVerified ||
                summary.netAmount <= 0
              }
              onClick={() => setOpenApproveDialog(true)}
            >
              Approve Payout
            </Button>
          </span>
        </Tooltip>
      </Box>

      {/* ===================== DIALOGS ===================== */}
      <Dialog
        open={openApproveDialog}
        onClose={() => setOpenApproveDialog(false)}
      >
        <DialogTitle>Confirm Approval</DialogTitle>
        <DialogContent>
          Are you sure you want to approve this payout? <br />
          <br />
          <strong>Net Amount:</strong> ₹{summary.netAmount.toFixed(2)}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenApproveDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleApprove}
            disabled={actionLoading}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ViewPayoutDetailsmodal;
